/**
 * Outil « Résolution graphique » (Boîte à outils, 3e → 1re).
 *
 * Deux courbes, ou une courbe et la droite y = k : on choisit le signe
 * (=, <, ≤, >, ≥) ; la bande où l'inégalité est vraie se colore, l'ensemble
 * des solutions se dessine sur l'axe des abscisses (crochets ouverts ou
 * fermés, points isolés) et s'écrit. Un curseur x compare f(x) et g(x) à la
 * verticale. Défi : choisir le bon ensemble parmi quatre, dont trois erreurs
 * classiques (crochets, sens, ordonnées lues au lieu des abscisses, domaine).
 * Logique : `solve.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { clamp, makeFrame, snap, type Frame } from '../../ui/frame';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { DragHandle, FIGURE_BG_CLASS, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  CMP_TEXT,
  MISTAKE_TEXT,
  SCENARIOS,
  cmpsFor,
  digitsOf,
  gOf,
  inDomain,
  inequationText,
  isSolution,
  listText,
  makeQuestion,
  num,
  options as makeOptions,
  readingText,
  rhsText,
  scenarioById,
  solve,
  type Cmp,
  type Fn,
  type SetOption,
  type Piece,
  type Question,
  type Scenario,
} from './solve';

const SIZE = 320;
const F_COLOR = 'var(--color-primary-500)';
const G_COLOR = 'var(--color-accent-500)';
const SOL = 'var(--color-success-600)';
const BAD = 'var(--color-error-500)';
const CMPS: Cmp[] = ['=', '<', '<=', '>', '>='];
const ROUNDS = 5;

/** chemin d'une courbe sur la fenêtre, coupé hors du domaine ou hors cadre */
function curvePath(fn: Fn, frame: Frame, inDomain: (x: number) => boolean): string {
  const { vp } = frame;
  const N = 480;
  let d = '';
  let pen = false;
  for (let i = 0; i <= N; i++) {
    const x = vp.xMin + ((vp.xMax - vp.xMin) * i) / N;
    const y = inDomain(x) ? fn.at(x) : NaN;
    if (!Number.isFinite(y) || y < vp.yMin - 3 || y > vp.yMax + 3) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${frame.tx(x).toFixed(1)},${frame.ty(y).toFixed(1)} `;
    pen = true;
  }
  return d;
}

const domainTest = (s: Scenario) => (x: number) => inDomain(s, x);

/** l'ensemble de définition, dit en mots de 3e (pas d'intervalle) ou noté */
function domainPhrase(s: Scenario, rank: number): string {
  if (rank > 4) return `sur ${s.domain.text}`;
  const d = s.domain;
  if (d.lo !== null && d.hi !== null) return `pour x compris entre ${num(d.lo)} et ${num(d.hi)}`;
  return 'pour tout nombre x';
}

/** « f(x) = x² », ou la courbe donnée */
function statement(s: Scenario, k: number, rank: number): string {
  const dom = domainPhrase(s, rank);
  const f = s.f.formula
    ? `f(x) = ${s.f.formula}, définie ${dom}`
    : `f est définie ${dom} par sa courbe`;
  const g = s.g
    ? s.g.formula === '0'
      ? 'on compare f(x) à 0'
      : `g(x) = ${s.g.formula}`
    : `droite d’équation y = ${num(k)}`;
  return `${f} ; ${g}.`;
}

/** crochet dessiné sur l'axe : tourné vers l'intérieur s'il est fermé */
function Bracket({
  x,
  y,
  side,
  closed,
}: {
  x: number;
  y: number;
  side: 'lo' | 'hi';
  closed: boolean;
}) {
  // « [ » à gauche fermé, « ] » à gauche ouvert (et l'inverse à droite)
  const dir = (side === 'lo') === closed ? 1 : -1;
  return (
    <path
      d={`M${x + 5 * dir},${y - 8} L${x},${y - 8} L${x},${y + 8} L${x + 5 * dir},${y + 8}`}
      fill="none"
      stroke={SOL}
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

function Figure({
  scenario,
  k,
  cmp,
  showSolution,
  judge,
  x,
  onX,
  onK,
}: {
  scenario: Scenario;
  k: number;
  cmp: Cmp;
  showSolution: boolean;
  /** le curseur dit-il (vert / rouge) si x est solution ? pas avant la réponse d'un défi */
  judge: boolean;
  x: number;
  onX: (x: number) => void;
  onK?: (k: number) => void;
}) {
  const frame = makeFrame(scenario.window, SIZE);
  const { vp, tx, ty } = frame;
  const svgRef = useRef<SVGSVGElement>(null);
  const g = gOf(scenario, k);
  const inD = domainTest(scenario);
  const sol = useMemo(() => solve(scenario, k, cmp), [scenario, k, cmp]);
  const kX = vp.xMax - (vp.xMax - vp.xMin) * 0.08;
  const ids = (onK ? ['M', 'K'] : ['M']) as Array<'M' | 'K'>;
  const drag = useDrag<'M' | 'K'>({
    svgRef,
    frame,
    ids,
    pointOf: (id) => (id === 'M' ? { x, y: 0 } : { x: kX, y: k }),
    onDrag: (id, p) => {
      if (id === 'M') onX(clamp(snap(p.x, 0.1), vp.xMin + 0.5, vp.xMax - 0.5));
      else onK?.(clamp(snap(p.y, 0.5), scenario.k!.min, scenario.k!.max));
    },
    onBackground: (p) => onX(clamp(snap(p.x, 0.1), vp.xMin + 0.5, vp.xMax - 0.5)),
    keyStep: 0.5,
  });

  const axisY = ty(0);
  const fx = inD(x) ? scenario.f.at(x) : NaN;
  const gx = inD(x) ? g.at(x) : NaN;
  const ok = isSolution(scenario, k, cmp, x);
  const xL = (v: number) => (v === -Infinity ? 0 : v === Infinity ? SIZE : tx(v));

  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className={cn(
          'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="group"
        aria-label={`Courbe de f et ${scenario.g ? 'courbe de g' : `droite y = ${num(k)}`}.`}
        data-testid="gs-figure"
        {...drag.svgProps}
      >
        {/* la bande où l'inégalité est vraie */}
        {showSolution &&
          sol.pieces
            .filter((p) => p.lo.v !== p.hi.v)
            .map((p, i) => (
              <rect
                key={`band${i}`}
                x={xL(p.lo.v)}
                y={0}
                width={Math.max(0, xL(p.hi.v) - xL(p.lo.v))}
                height={SIZE}
                fill="var(--color-success-500)"
                opacity={0.12}
              />
            ))}
        <PlotGrid frame={frame} step={1} />
        <path d={curvePath(g, frame, inD)} fill="none" stroke={G_COLOR} strokeWidth={2.5} />
        <path d={curvePath(scenario.f, frame, inD)} fill="none" stroke={F_COLOR} strokeWidth={3} />
        {/* extrémités d'une courbe définie sur un segment */}
        {[scenario.domain.lo, scenario.domain.hi].map((b, i) =>
          b === null || !Number.isFinite(scenario.f.at(b)) ? null : (
            <circle key={`end${i}`} cx={tx(b)} cy={ty(scenario.f.at(b))} r={4} fill={F_COLOR} />
          ),
        )}

        {/* points d'intersection et leurs abscisses */}
        {showSolution &&
          sol.zeros.map((z) => {
            const y = scenario.f.at(z);
            if (!Number.isFinite(y) || y < vp.yMin || y > vp.yMax) return null;
            return (
              <g key={`z${z}`}>
                <line
                  x1={tx(z)}
                  y1={ty(y)}
                  x2={tx(z)}
                  y2={axisY}
                  stroke="var(--color-ink-500)"
                  strokeDasharray="4 4"
                />
                <circle cx={tx(z)} cy={ty(y)} r={4.5} fill="var(--color-ink-900)" />
              </g>
            );
          })}

        {/* l'ensemble des solutions sur l'axe des abscisses */}
        {showSolution && (
          <g data-testid="gs-axis-set">
            {sol.pieces.map((p: Piece, i) =>
              p.lo.v === p.hi.v ? (
                <circle key={`p${i}`} cx={tx(p.lo.v)} cy={axisY} r={5.5} fill={SOL} />
              ) : (
                <g key={`s${i}`}>
                  <line
                    x1={xL(p.lo.v)}
                    x2={xL(p.hi.v)}
                    y1={axisY}
                    y2={axisY}
                    stroke={SOL}
                    strokeWidth={5}
                    strokeLinecap="butt"
                  />
                  {Number.isFinite(p.lo.v) && (
                    <Bracket x={tx(p.lo.v)} y={axisY} side="lo" closed={p.lo.closed} />
                  )}
                  {Number.isFinite(p.hi.v) && (
                    <Bracket x={tx(p.hi.v)} y={axisY} side="hi" closed={p.hi.closed} />
                  )}
                </g>
              ),
            )}
          </g>
        )}

        {/* le curseur : f(x) et g(x) à la verticale de x */}
        <line
          x1={tx(x)}
          x2={tx(x)}
          y1={0}
          y2={SIZE}
          stroke="var(--color-ink-400)"
          strokeDasharray="2 5"
        />
        {Number.isFinite(fx) && Number.isFinite(gx) && (
          <g>
            <line
              x1={tx(x)}
              x2={tx(x)}
              y1={ty(clamp(fx, vp.yMin, vp.yMax))}
              y2={ty(clamp(gx, vp.yMin, vp.yMax))}
              stroke={!judge ? 'var(--color-ink-500)' : ok ? SOL : BAD}
              data-testid="gs-cursor-seg"
              data-verdict={!judge ? 'hidden' : ok ? 'ok' : 'no'}
              strokeWidth={3}
            />
            {fx >= vp.yMin && fx <= vp.yMax && (
              <circle cx={tx(x)} cy={ty(fx)} r={4.5} fill={F_COLOR} />
            )}
            {gx >= vp.yMin && gx <= vp.yMax && (
              <circle cx={tx(x)} cy={ty(gx)} r={4.5} fill={G_COLOR} />
            )}
          </g>
        )}
        <DragHandle
          x={tx(x)}
          y={axisY}
          color="var(--color-ink-900)"
          label="x"
          labelDy={20}
          name={`Curseur d’abscisse ${num(x, 1)}`}
          active={drag.active === 'M'}
          keyProps={drag.keyProps('M')}
        />
        {onK && (
          <DragHandle
            x={tx(kX)}
            y={ty(k)}
            color={G_COLOR}
            label="k"
            labelDx={-16}
            name={`Droite y = ${num(k)}`}
            active={drag.active === 'K'}
            keyProps={drag.keyProps('K')}
          />
        )}
      </svg>
      <div className="text-ink-700 mt-1.5 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="bg-primary-500 inline-block h-1 w-5 rounded-full" />
          courbe de f
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="bg-accent-500 inline-block h-1 w-5 rounded-full" />
          {scenario.g
            ? scenario.g.formula === '0'
              ? 'axe des abscisses (y = 0)'
              : 'courbe de g'
            : `droite y = ${num(k)}`}
        </span>
      </div>
    </div>
  );
}

const CMP_NAME: Record<Cmp, string> = {
  '=': 'égal',
  '<': 'strictement inférieur',
  '<=': 'inférieur ou égal',
  '>': 'strictement supérieur',
  '>=': 'supérieur ou égal',
};

/** les cinq signes, en cibles de 44 px */
function SignPicker({ value, onChange }: { value: Cmp; onChange: (c: Cmp) => void }) {
  return (
    <div
      role="group"
      aria-label="Signe"
      className="bg-ink-100 inline-flex rounded-xl p-1 text-base font-semibold"
    >
      {CMPS.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={CMP_NAME[c]}
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={cn(
            'min-h-11 min-w-11 rounded-lg px-2 transition-colors',
            value === c ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
          )}
        >
          {CMP_TEXT[c]}
        </button>
      ))}
    </div>
  );
}

/** « En x = 1,5 : f(x) = 2,25 et g(x) = 3,5, donc f(x) < g(x). » */
function cursorText(s: Scenario, k: number, cmp: Cmp, x: number): string {
  const ok = isSolution(s, k, cmp, x);
  const xs = num(x, 1);
  if (ok === null) return `${xs} n’est pas dans l’ensemble de définition ${s.domain.text}.`;
  const fv = s.f.at(x);
  const gv = gOf(s, k).at(x);
  const atNode = !s.f.nodes || s.f.nodes.some(([nx]) => Math.abs(nx - x) < 1e-9);
  const fText = `${atNode ? '=' : '≈'} ${num(fv, atNode ? 2 : 1)}`;
  const rhs = rhsText(s, k);
  // g(x) seulement si g n'est pas une constante
  const gPart = rhs === 'g(x)' ? ` et g(x) = ${num(gv, 2)}` : '';
  const rel = Math.abs(fv - gv) < 1e-9 ? '=' : fv < gv ? '<' : '>';
  return `En x = ${xs} : f(x) ${fText}${gPart}, donc f(x) ${rel} ${rhs} : ${xs} ${ok ? 'est' : 'n’est pas'} solution de ${inequationText(s, k, cmp)}.`;
}

function Explore({ rank }: { rank: number }) {
  const cmps = cmpsFor(rank);
  const list = SCENARIOS.filter((s) => s.minRank <= rank && rank <= s.maxRank);
  const [id, setId] = useState(list[0]!.id);
  const scenario = scenarioById(id);
  const [k, setK] = useState(scenario.k?.start ?? 0);
  const [cmp, setCmp] = useState<Cmp>(cmps.includes('>=') ? '>=' : '=');
  const [x, setX] = useState(0.5);
  const [show, setShow] = useState(true);
  const sol = useMemo(() => solve(scenario, k, cmp), [scenario, k, cmp]);

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Courbes" className="flex flex-wrap gap-1.5">
        {list.map((s) => (
          <ToggleChip
            key={s.id}
            on={s.id === id}
            onClick={() => {
              setId(s.id);
              setK(s.k?.start ?? 0);
              setX(0.5);
            }}
          >
            {s.label}
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-700 text-sm">{frenchSpaces(statement(scenario, k, rank))}</p>
      <div className="flex flex-wrap items-center gap-2">
        {cmps.length > 1 && <SignPicker value={cmp} onChange={setCmp} />}
        {scenario.k && (
          <div className="w-36">
            <Stepper
              label="k"
              value={k}
              min={scenario.k.min}
              max={scenario.k.max}
              step={0.5}
              format={(v) => num(v)}
              onChange={setK}
            />
          </div>
        )}
      </div>
      <p className="text-ink-900 text-base font-semibold">
        {frenchSpaces(`Résoudre ${inequationText(scenario, k, cmp)}`)}
      </p>
      <Figure
        scenario={scenario}
        k={k}
        cmp={cmp}
        showSolution={show}
        judge
        x={x}
        onX={setX}
        onK={scenario.k ? setK : undefined}
      />
      <p className="text-ink-700 text-sm" aria-live="polite" data-testid="gs-cursor">
        {frenchSpaces(cursorText(scenario, k, cmp, x))}
      </p>
      <ToggleChip on={show} onClick={() => setShow(!show)}>
        Voir la solution
      </ToggleChip>
      {show && (
        <Note live={false} tone="success">
          <span className="block text-base font-semibold" data-testid="gs-set" aria-live="polite">
            {frenchSpaces(rank <= 4 ? listText(sol.pieces, digitsOf(scenario)) : sol.text)}
          </span>
          <span className="mt-1 block">{frenchSpaces(readingText(scenario, k, cmp))}</span>
          {sol.approx && (
            <span className="mt-1 block">
              {frenchSpaces(
                `Les valeurs notées ≈ sont lues sur le graphique, au ${digitsOf(scenario) === 1 ? 'dixième' : 'centième'} près.`,
              )}
            </span>
          )}
        </Note>
      )}
    </div>
  );
}

function Challenge({ rank, seed }: { rank: number; seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const fresh = (): { q: Question; opts: SetOption[] } => {
    const q = makeQuestion(rank, rnd);
    return { q, opts: makeOptions(q.scenario, q.k, q.cmp, rnd, rank <= 4 ? 'list' : 'set') };
  };
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const [round, setRound] = useState(() => fresh());
  const [count, setCount] = useState(1);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [x, setX] = useState(0.5);
  const { q, opts } = round;
  const done = picked !== null && count >= ROUNDS;
  const chosen = picked === null ? null : opts[picked]!;
  const correct = opts.find((o) => o.correct)!;

  const pick = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (opts[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    if (done) {
      setCount(1);
      setScore(0);
    } else setCount(count + 1);
    setRound(fresh());
    setPicked(null);
    setX(0.5);
    moved.current = true;
  };
  // le bouton « Question suivante » disparaît : le focus va sur la nouvelle consigne
  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [round]);

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        Question {count} / {ROUNDS} · {score} juste{score > 1 ? 's' : ''}
      </p>
      <p className="text-ink-700 text-sm">{frenchSpaces(statement(q.scenario, q.k, rank))}</p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(`Résous graphiquement ${inequationText(q.scenario, q.k, q.cmp)}.`)}
      </p>
      <Figure
        scenario={q.scenario}
        k={q.k}
        cmp={q.cmp}
        showSolution={picked !== null}
        judge={picked !== null}
        x={x}
        onX={setX}
      />
      <div role="group" aria-label="Réponses" className="grid gap-2">
        {opts.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => pick(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-left text-sm font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      {chosen && (
        <Note live tone={chosen.correct ? 'success' : 'error'}>
          {frenchSpaces(
            chosen.correct
              ? `Exact. ${readingText(q.scenario, q.k, q.cmp)}`
              : `${MISTAKE_TEXT[chosen.kind ?? 'other']} La bonne réponse : ${correct.text}.`,
          )}
        </Note>
      )}
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">{`Score : ${score} / ${ROUNDS}`}</p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function GraphicSolveTool({
  level,
  seed,
}: {
  level?: string | null;
  /** graine des défis (tests) */
  seed?: number;
}) {
  const rank = Math.min(6, Math.max(4, schoolLevelRank(level) ?? 5));
  const [mode, setMode] = useState<'explore' | 'challenge'>('explore');
  // le défi prépare ses questions à la première ouverture seulement
  const [challengeOpened, setChallengeOpened] = useState(false);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'challenge', label: 'Défi' },
        ]}
        onChange={(m) => {
          setMode(m);
          if (m === 'challenge') setChallengeOpened(true);
        }}
      />
      {/* les deux restent montés : on garde le défi en cours */}
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'challenge'}>
        {challengeOpened && <Challenge rank={rank} seed={seed} />}
      </div>
    </div>
  );
}
